
import React from "react";
import { useQuery } from "@tanstack/react-query";
import { supabaseWorker } from "@/integrations/supabase/workerClient";
import type { Client } from "@/types/entities/client";
import type { LastAction as RentalsLastAction } from "@/components/rentals/dialogs/hooks/useRentalInventory";
import type { LastAction as OrdersLastAction } from "@/domains/orders/hooks/types";
import type { RentalFormData } from "@/components/rentals/dialogs/hooks/useRentalFormState";

// Import shared sections from AddOrderDialog
import { ClientSelectionSection } from "@/components/common/dialogs/sections/ClientSelectionSection";
import { LocationSelectionSection } from "@/components/common/dialogs/sections/LocationSelectionSection";
import { DateSelectionSection } from "@/components/common/dialogs/sections/DateSelectionSection";
import { StatusSelectionSection } from "@/components/common/dialogs/sections/StatusSelectionSection";
import { FinancialSection } from "@/components/common/dialogs/sections/FinancialSection";
import { InventorySelectionSection } from "@/components/orders/dialogs/components/sections/InventorySelectionSection";
import { RentalKitsSection } from "@/components/orders/dialogs/components/sections/RentalKitsSection";
import { DocumentSignatureSection } from "@/components/orders/dialogs/components/sections/DocumentSignatureSection";
import { useRentalKitsUnified } from '@/hooks/rentals/useRentalKitsUnified';
import { useInventoryUnified } from '@/hooks/useInventoryUnified';
import { useRentalStatuses } from '@/hooks/domain-statuses/useRentalStatuses';
import { usePaymentStatuses } from '@/hooks/domain-statuses/usePaymentStatuses';
import { usePaymentMethods } from '@/hooks/domain-statuses/usePaymentMethods';

// Используем фактическую форму данных из хука, чтобы унифицировать типизацию между диалогом и секциями
type RentalDialogFormShape = RentalFormData;

interface RentalDialogSectionsProps {
  formData: RentalDialogFormShape;
  selectedKits: string[];
  clients: Client[];
  clientsLoading: boolean;
  currentRentalPoint: { id: string; name: string } | null;
  totalAmount: number;
  lastAction: RentalsLastAction | null;
  isEditMode?: boolean; // Добавляем параметр для различения создания и редактирования
  onClientChange: (clientId: string) => void;
  onAddClient: () => void;
  onDateChange: (field: 'startDate' | 'endDate' | 'tariffId', value: string | Date | null) => void;
  onKitsChange: (kits: string[]) => void;
  onInventoryItemChange: (id: string, quantity: number) => void;
  onInventoryChange: (id: string, isSelected: boolean) => void;
  onUndoLastAction: () => void;
  onFieldChange: <K extends keyof RentalFormData>(field: K, value: RentalFormData[K]) => void;
}

export function RentalDialogSections({
  formData,
  selectedKits,
  clients,
  clientsLoading,
  currentRentalPoint,
  totalAmount,
  lastAction,
  isEditMode: _isEditMode = false,
  onClientChange,
  onAddClient,
  onDateChange: _onDateChange,
  onKitsChange,
  onInventoryItemChange,
  onInventoryChange,
  onUndoLastAction,
  onFieldChange
}: RentalDialogSectionsProps) {
  // RentalFormData не хранит поля generateContract/signatureId. Создаем локальный обработчик,
  // который удовлетворяет сигнатуре DocumentSignatureSection и безопасно игнорирует изменения.
  const handleSignatureChange = (
    _field: 'generateContract' | 'signatureId',
    _value: boolean | string
  ): void => {
    // В текущей реализации диалога аренды эти поля не используются.
    // Помечаем параметры как "использованные", чтобы убрать предупреждения линтера.
    void _field;
    void _value;
    // Если потребуется — будет добавлено хранилище в форму по отдельной задаче.
  };
  // Загружаем все пункты проката для выбора пункта возврата
  const {
    data: rentalPoints = []
  } = useQuery({
    queryKey: ['rental-points'],
    queryFn: async () => {
      const {
        data,
        error
      } = await supabaseWorker.from('rental_points').select('id, name, address').order('name');
      if (error) throw error;
      return data || [];
    }
  });

  // Загружаем комплекты аренды
  const { kits: rentalKits, isLoading: kitsLoading } = useRentalKitsUnified();

  // Загружаем инвентарь для аренды
  const { inventory: inventoryItems, loading: inventoryLoading } = useInventoryUnified({ 
    rentalPointId: currentRentalPoint?.id 
  });

  // Load domain statuses from new system
  const { rentalStatuses = [], isLoading: _statusesLoading } = useRentalStatuses();
  const { paymentStatuses = [], isLoading: _paymentStatusesLoading } = usePaymentStatuses();
  const { paymentMethods = [], isLoading: _paymentMethodsLoading } = usePaymentMethods();

  // Filter rental statuses based on dialog mode
  const filteredRentalStatuses = rentalStatuses; // Показываем все доступные статусы и при создании, и при редактировании

  // Convert to options format
  // IMPORTANT: Use status.name as value so downstream mappers can normalize to DB enum
  const statusOptions = filteredRentalStatuses.map(status => ({
    value: status.name,
    label: status.name
  }));

  const paymentStatusOptions = paymentStatuses.map(status => ({
    value: status.id,
    label: status.name
  }));

  const paymentMethodOptions = paymentMethods.map(method => ({
    value: method.id,
    label: method.name
  }));

  return <div className="space-y-6 py-4 px-6">
      {/* Client Selection - now using shared component */}
      <ClientSelectionSection selectedClientId={formData.clientId} clients={clients} onClientChange={onClientChange} onAddClient={onAddClient} isLoading={clientsLoading} />


      {/* Location Selection - using shared component */}
      <LocationSelectionSection 
        rentalPointName={currentRentalPoint?.name || "Не выбран"}
        returnPointName={formData.returnPointId ? rentalPoints.find(p => p.id === formData.returnPointId)?.name || "Не выбран" : "Не выбран"}
        showReturnPoint={Boolean(formData.returnPointId)}
        onShowReturnPointChange={(show) => {
          // При выключении скрываем пункт возврата, очищая returnPointId
          if (!show) onFieldChange('returnPointId', '');
        }}
        rentalPoints={rentalPoints}
        returnPointId={formData.returnPointId || ''}
        onReturnPointChange={(id) => onFieldChange('returnPointId', id)}
        isLoading={false}
      />


      {/* Date Selection - using shared component with time */}
      <DateSelectionSection 
        tariffId={formData.tariffId || ''}
        startDate={formData.startDate ? new Date(formData.startDate) : null}
        endDate={formData.endDate ? new Date(formData.endDate) : null}
        onTariffChange={(tariffId) => onFieldChange('tariffId', tariffId)}
        onStartDateChange={(date) => onFieldChange('startDate', date)}
        onEndDateChange={(date) => onFieldChange('endDate', date)}
      />


      {/* Status Selection - using shared component */}
      <StatusSelectionSection 
        status={formData.status || ''}
        paymentStatus={formData.paymentStatus || ''}
        paymentMethodId={formData.paymentMethodId || ''}
        onStatusChange={status => onFieldChange('status', status)}
        onPaymentStatusChange={paymentStatus => onFieldChange('paymentStatus', paymentStatus)}
        onPaymentMethodChange={method => onFieldChange('paymentMethodId', method)}
        statusOptions={statusOptions}
        paymentStatusOptions={paymentStatusOptions}
        paymentMethodOptions={paymentMethodOptions}
      />


      {/* Inventory Selection - using shared component */}
      <InventorySelectionSection 
        selectedItems={formData.inventoryItems || []} 
        availableInventoryItems={inventoryItems || []}
        inventoryLoading={inventoryLoading}
        lastAction={lastAction as unknown as OrdersLastAction}
        onInventoryItemChange={onInventoryItemChange} 
        onUndoLastAction={onUndoLastAction} 
      />


      {/* Rental Kits Selection - using shared component */}
      <RentalKitsSection 
        selectedKits={selectedKits} 
        rentalKits={rentalKits}
        kitsLoading={kitsLoading}
        onKitsChange={onKitsChange} 
      />


      {/* Financial Section - using shared component */}
      <FinancialSection discount={formData.discount || 0} depositAmount={formData.depositAmount || 0} promoCode={formData.promoCode || ''} totalAmount={totalAmount} onDiscountChange={discount => onFieldChange('discount', discount)} onDepositAmountChange={amount => onFieldChange('depositAmount', amount)} onPromoCodeChange={code => onFieldChange('promoCode', code)} />


      {/* Document Signature Section - using shared component */}
      <DocumentSignatureSection
        signatureId={''}
        clientId={formData.clientId}
        generateContract={false}
        onChange={handleSignatureChange}
      />
    </div>;
}
